/**
 * PdfPreviewDialog - Modal de visualização inline de PDF com botão de download.
 * Padrão aplicado em toda a app (Compras, Pedidos, etc).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string
 *  - apiPath: caminho relativo à /api (ex: '/cotacoes/x/pdf')
 *  - filename: nome do arquivo no download
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Loader2, X } from 'lucide-react';
import { fetchPdfBlobUrl } from '../lib/previewAuthed';
import { downloadAuthed } from '../lib/downloadAuthed';

const PdfPreviewDialog = ({ open, onClose, title, apiPath, filename }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    const load = async () => {
      if (!open || !apiPath) return;
      setLoading(true);
      const u = await fetchPdfBlobUrl(apiPath, 'Erro ao carregar PDF');
      if (cancelled) {
        if (u) URL.revokeObjectURL(u);
        return;
      }
      url = u;
      setBlobUrl(u);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [open, apiPath]);

  const handleDownload = async () => {
    await downloadAuthed(apiPath, filename, 'Erro ao baixar PDF');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0"
        data-testid="pdf-preview-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-base flex items-center justify-between gap-2">
            <span className="truncate">{title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-slate-100">
          {loading && (
            <div className="h-full flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando PDF...
            </div>
          )}
          {!loading && blobUrl && (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-full border-0"
              data-testid="pdf-preview-iframe"
            />
          )}
          {!loading && !blobUrl && (
            <div className="h-full flex items-center justify-center text-slate-500 px-4 text-center">
              Não foi possível carregar o PDF. Tente novamente.
            </div>
          )}
        </div>
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pdf-preview-close">
            <X className="w-3 h-3 mr-1" /> Fechar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!blobUrl}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="pdf-preview-download"
          >
            <Download className="w-3 h-3 mr-1" /> Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfPreviewDialog;
